/**
 * The marketplace's Sprays category. Equip is global, same shape as a charm — one spray worn at
 * a time. Backend contract per the marketplace rebuild brief (2026-09-16); the endpoints may not
 * exist yet, which is fine — a failed catalogue fetch just hides this tab (`marketplace-section`).
 */
import { confirmAction } from '../ui';
import { formatNumber, t } from '../../i18n';
import { equipSpray, fetchSpraysCatalogue, purchaseSpray, type SprayCatalogueView, type SprayEntry } from '../sprays-api';
import { spraySwatch, type MarketItem } from './marketplace-tile';
import { createMarketSection, type MarketSection } from './marketplace-section';

function hex(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

function toItem(entry: SprayEntry, act: (mutate: () => Promise<unknown>) => void, busy: boolean): MarketItem {
  const currencyLabel = entry.currency === 'PREMIUM' ? t('common.gems') : t('common.credits');

  return {
    key: `spray-${entry.id}`,
    category: 'sprays',
    name: entry.name,
    description: entry.description,
    art: spraySwatch(entry.glyph, hex(entry.tint)),
    price: { amount: entry.priceCoins, currency: entry.currency },
    owned: entry.owned,
    equipped: entry.equipped,
    affordable: entry.affordable,
    action: !entry.owned
      ? {
          label: entry.affordable ? t('common.buy') : entry.currency === 'PREMIUM' ? t('shop.needGems') : t('shop.needCredits'),
          variant: entry.affordable ? 'primary' : 'ghost',
          disabled: busy || !entry.affordable,
          onClick: () =>
            void (async () => {
              const confirmed = await confirmAction({
                title: t('confirmDialog.buyTitle'),
                body: t('confirmDialog.buyBody', { item: entry.name, price: `${formatNumber(entry.priceCoins)} ${currencyLabel}` }),
              });
              if (!confirmed) return;
              act(() => purchaseSpray(entry.id));
            })(),
        }
      : !entry.equipped
        ? {
            label: t('common.equip'),
            variant: 'secondary',
            disabled: busy,
            onClick: () =>
              void (async () => {
                const confirmed = await confirmAction({
                  title: t('confirmDialog.equipTitle'),
                  body: t('confirmDialog.equipBody', { item: entry.name }),
                });
                if (!confirmed) return;
                act(() => equipSpray(entry.id));
              })(),
          }
        : {
            label: t('common.remove'),
            variant: 'ghost',
            disabled: busy,
            onClick: () => act(() => equipSpray(0)),
          },
  };
}

export function createSpraysSection(): MarketSection {
  return createMarketSection<SprayCatalogueView>({
    id: 'sprays',
    label: t('shop.sprays.title'),
    note: t('shop.sprays.note'),
    fetchCatalogue: fetchSpraysCatalogue,
    toItems: (catalogue, act, busy) => catalogue.sprays.map((entry) => toItem(entry, act, busy)),
  });
}
